import {getCourseDocumentRef} from '@cip/core/firebase/client';
import {doc, type FirestoreDataConverter} from 'firebase/firestore';
import type {CourseId} from 'types/common';
import {
  validateArrayField,
  validateOptionalPrimitiveField,
} from 'utils/general';

export interface CourseThemeDocument {
  /**
   * The color of the main course navbar.
   */
  navbarColor?: string;

  /**
   * The URL of the logo to display in the main course navbar. If this field is
   * provided, `navbarLogoText` is ignored.
   */
  navbarLogoImageUrl?: string;

  /**
   * The text to display in the main course navbar as the "logo" text. If
   * `navbarLogoImageUrl` is provided, this field is ignored.
   */
  navbarLogoText?: string;

  /**
   * The font family to use for most text in the course. The site defaults to
   * Nunito if this field is not provided.
   */
  mainBodyFontFamily?: string;

  /**
   * The font weight to use for most text in the course. A weight of 0 is
   * ignored.
   */
  mainBodyFontWeight?: number;
  /**
   * The font family to use for the h1 header in the course. The site defaults
   * to Nunito if this field is not provided.
   */
  h1FontFamily?: string;

  /**
   * The font weight to use for the h1 header in the course. A weight of 0 is
   * ignored.
   */
  h1FontWeight?: number;
  /**
   * The font family to use for the h2 header in the course. The site defaults
   * to Nunito if this field is not provided.
   */
  h2FontFamily?: string;

  /**
   * The font weight to use for the h2 header in the course. A weight of 0 is
   * ignored.
   */
  h2FontWeight?: number;

  /**
   * The font family to use for the h3 header in the course. The site defaults
   * to Nunito if this field is not provided.
   */
  h3FontFamily?: string;

  /**
   * The font weight to use for the h3 header in the course. A weight of 0 is
   * ignored.
   */
  h3FontWeight?: number;

  /**
   * The font family to use for the h4 header in the course. The site defaults
   * to Nunito if this field is not provided.
   */
  h4FontFamily?: string;

  /**
   * The font weight to use for the h4 header in the course. A weight of 0 is
   * ignored.
   */
  h4FontWeight?: number;

  /**
   * The font family to use for the h5 header in the course. The site defaults
   * to Nunito if this field is not provided.
   */
  h5FontFamily?: string;

  /**
   * The font weight to use for the h5 header in the course. A weight of 0 is
   * ignored.
   */
  h5FontWeight?: number;

  /**
   * The font family to use for code blocks in the course. The site defaults
   * to Monaco if this field is not provided.
   */
  codeFontFamily?: string;

  /**
   * The font weight to use for code blocks in the course. A weight of 0 is
   * ignored.
   */
  codeFontWeight?: number;

  /**
   * The URLs of additional fonts to use for the course.
   */
  remoteFontUrls?: string[];

  /**
   * Login page title text.
   */
  loginPageTitle?: string;

  /**
   * Login page image url.
   */
  loginPageImageUrl?: string;

  /**
   * Primary button color.
   */
  buttonColorPrimary?: string;

  /**
   * The logo to use in splash pages.
   */
  splashPageLogoUrl?: string;

  /**
   * The background color of body elements. Defaults to Bootstrap's defaults.
   */
  bodyBackgroundColor?: string;

  /**
   * The foreground/text color for body text. Pairs with
   * {@link bodyBackgroundColor} to enable a full light-on-dark (or dark-on-light)
   * theme. When omitted, Bootstrap's default body color is used.
   */
  bodyTextColor?: string;

  /**
   * The background color for raised surfaces — cards, panels, and the
   * application splash/form containers that sit above the body background. When
   * omitted, each surface keeps its own default (e.g. the application card's
   * translucent white).
   */
  surfaceColor?: string;

  /**
   * The foreground/text color for content on {@link surfaceColor} surfaces. When
   * omitted, {@link bodyTextColor} (or each surface's own default) applies.
   */
  surfaceTextColor?: string;

  /**
   * The color for hyperlinks and link-styled actions. When omitted, Bootstrap's
   * default link color is used.
   */
  linkColor?: string;

  /**
   * The email to use to contact the course team. For Code in Place, this is
   * typically codeinplace@stanford.edu.
   */
  contactEmail?: string;
  /**
   * The brand name to use for the course. For Code in Place, this is typically
   * "Code in Place". For PYLATINO, this is typically "PYLATINO".
   */
  brandName?: string;

  /**
   * The name of the hosting institution. This differs from brandName in that
   * brandName is for the course (e.g., "Code in Place"), while institutionName
   * is for the hosting institution (e.g., "Stanford University" or "Universidad
   * de los Andes").
   */
  institutionName?: string;
}

const isCourseThemeDocument = (data: unknown): data is CourseThemeDocument => {
  if (typeof data !== 'object' || !data) {
    return false;
  }

  const courseTheme = data as CourseThemeDocument;

  return (
    validateOptionalPrimitiveField(courseTheme.navbarColor, 'string') &&
    validateOptionalPrimitiveField(courseTheme.navbarLogoImageUrl, 'string') &&
    validateOptionalPrimitiveField(courseTheme.navbarLogoText, 'string') &&
    validateOptionalPrimitiveField(courseTheme.mainBodyFontFamily, 'string') &&
    validateOptionalPrimitiveField(courseTheme.mainBodyFontWeight, 'number') &&
    validateOptionalPrimitiveField(courseTheme.h1FontFamily, 'string') &&
    validateOptionalPrimitiveField(courseTheme.h1FontWeight, 'number') &&
    validateOptionalPrimitiveField(courseTheme.h2FontFamily, 'string') &&
    validateOptionalPrimitiveField(courseTheme.h2FontWeight, 'number') &&
    validateOptionalPrimitiveField(courseTheme.h3FontFamily, 'string') &&
    validateOptionalPrimitiveField(courseTheme.h3FontWeight, 'number') &&
    validateOptionalPrimitiveField(courseTheme.h4FontFamily, 'string') &&
    validateOptionalPrimitiveField(courseTheme.h4FontWeight, 'number') &&
    validateOptionalPrimitiveField(courseTheme.h5FontFamily, 'string') &&
    validateOptionalPrimitiveField(courseTheme.h5FontWeight, 'number') &&
    validateOptionalPrimitiveField(courseTheme.codeFontFamily, 'string') &&
    validateOptionalPrimitiveField(courseTheme.codeFontWeight, 'number') &&
    validateOptionalPrimitiveField(courseTheme.loginPageTitle, 'string') &&
    validateOptionalPrimitiveField(courseTheme.loginPageImageUrl, 'string') &&
    validateOptionalPrimitiveField(courseTheme.buttonColorPrimary, 'string') &&
    validateArrayField({
      field: courseTheme.remoteFontUrls,
      validateElement: url => typeof url === 'string',
      isOptional: true,
    }) &&
    validateOptionalPrimitiveField(courseTheme.splashPageLogoUrl, 'string') &&
    validateOptionalPrimitiveField(courseTheme.bodyBackgroundColor, 'string') &&
    validateOptionalPrimitiveField(courseTheme.bodyTextColor, 'string') &&
    validateOptionalPrimitiveField(courseTheme.surfaceColor, 'string') &&
    validateOptionalPrimitiveField(courseTheme.surfaceTextColor, 'string') &&
    validateOptionalPrimitiveField(courseTheme.linkColor, 'string') &&
    validateOptionalPrimitiveField(courseTheme.contactEmail, 'string') &&
    validateOptionalPrimitiveField(courseTheme.brandName, 'string') &&
    validateOptionalPrimitiveField(courseTheme.institutionName, 'string')
  );
};

const COURSE_THEME_DOCUMENT_CONVERTER: FirestoreDataConverter<CourseThemeDocument | null> =
  {
    toFirestore: (courseTheme: CourseThemeDocument) => {
      return courseTheme;
    },

    fromFirestore: (snapshot, options) => {
      if (!snapshot.exists) {
        return null;
      }

      const data = snapshot.data(options);
      if (!data) {
        return null;
      }

      if (!isCourseThemeDocument(data)) {
        throw new Error('Invalid course theme document');
      }

      return data as CourseThemeDocument;
    },
  };

export const getCourseThemeDocumentRef = (courseId: CourseId) => {
  return doc(getCourseDocumentRef({courseId}), 'docs/theme').withConverter(
    COURSE_THEME_DOCUMENT_CONVERTER,
  );
};
